import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from './admin-api';
import { api } from '../../api/client';
import { CodeText } from '../../components/Code';

export default function Demo() {
  const qc = useQueryClient();
  const signals = useQuery({ queryKey: ['signals'], queryFn: api.signals });
  const [selectedSignal, setSelectedSignal] = useState('S-02');
  const [injectValue, setInjectValue] = useState(1.0);
  const [msg, setMsg] = useState<string | null>(null);

  const reset = useMutation({
    mutationFn: admin.reset,
    onSuccess: async (data) => {
      await qc.invalidateQueries();
      setMsg(`Demo state reset at ${new Date(data.reseeded_at).toLocaleTimeString()}.`);
    },
    onError: (e) => setMsg(`Reset failed: ${e instanceof Error ? e.message : 'unknown'}`),
  });

  const reseed = useMutation({
    mutationFn: admin.reseed,
    onSuccess: async (data) => {
      await qc.invalidateQueries();
      setMsg(`Canonical data reseeded at ${new Date(data.reseeded_at).toLocaleTimeString()}.`);
    },
    onError: (e) => setMsg(`Reseed failed: ${e instanceof Error ? e.message : 'unknown'}`),
  });

  const inject = useMutation({
    mutationFn: () => admin.injectSignal(selectedSignal, injectValue),
    onSuccess: async (data) => {
      await qc.invalidateQueries();
      const signalName = signals.data?.find((s) => s.id === selectedSignal)?.name ?? 'selected signal';
      setMsg(`Injected ${signalName} = ${injectValue} · breached=${data.breached}`);
    },
    onError: (e) => setMsg(`Inject failed: ${e instanceof Error ? e.message : 'unknown'}`),
  });

  return (
    <div>
      <div className="ph">Admin · Demo Controls</div>
      <div className="h2">Reset, reseed, fire signals</div>
      <p className="lead">Operator controls for a clean walk-through. Use these to prep the demo state before a CTO meeting.</p>

      {msg && <div className="panel" style={{ marginBottom: 14, borderColor: 'var(--olive)' }}><div className="small" style={{ color: 'var(--olive)' }}>{msg}</div></div>}

      <div className="grid2">
        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Reset demo state</div>
          <div className="small" style={{ marginBottom: 12 }}>
            Drops drift packets, decision log, weight history nudges, and resets all signal current_values to zero.
            Canonical spine/twins/options stay.
          </div>
          <button type="button" className="btn primary" onClick={() => reset.mutate()} disabled={reset.isPending}>
            {reset.isPending ? 'Resetting…' : '↻ Reset demo state'}
          </button>
        </div>

        <div className="panel">
          <div className="qhead"><span className="qdot" style={{ background: 'var(--red)' }} />Full reseed</div>
          <div className="small" style={{ marginBottom: 12 }}>
            Drops everything and reseeds from `spine_data.py` + `az_data.py` + the synthetic <CodeText text="O-17" compact /> reprice option.
            Use if data drift is severe.
          </div>
          <button type="button" className="btn ghost" onClick={() => reseed.mutate()} disabled={reseed.isPending}>
            {reseed.isPending ? 'Reseeding…' : 'Full reseed'}
          </button>
        </div>
      </div>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--teal)' }} />Fire signal</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px auto', gap: 8, alignItems: 'end' }}>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Signal</label>
            <select className="c2-input" value={selectedSignal} onChange={(e) => setSelectedSignal(e.target.value)}>
              {signals.data?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="small faint" style={{ display: 'block', marginBottom: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Value</label>
            <input className="c2-input" type="number" min={0} max={2} step={0.1} value={injectValue} onChange={(e) => setInjectValue(Number(e.target.value))} />
          </div>
          <button type="button" className="btn primary" onClick={() => inject.mutate()} disabled={inject.isPending}>
            {inject.isPending ? 'Injecting…' : '⚡ Inject'}
          </button>
        </div>
        <div className="small faint" style={{ marginTop: 8 }}>
          Tripwire rule: <span className="mono">value &gt;= 1.0</span> triggers a breach + drift packet.
        </div>
      </div>
    </div>
  );
}
